/**
 * star_field — the night sky's stars as ROUND POINTS ON THE SPHERE (2026-09-11).
 *
 * Every catalogued star (lib/sky/star_catalog.ts — extracted from the
 * astrophoto by tools/asset-pipeline/extract-sky-stars.mjs) is drawn
 * analytically: a Gaussian core around its direction plus a soft
 * photographic halo, sized in ANGLE (chord units, like the traffic dots), so
 * a star is the same round disc at the zenith and at the horizon, crisp on a
 * 4K screen and a phone alike. The equirect photo could do neither: its
 * stars were the upscaler's streaky texels, and at the pole a texel row spans
 * a circle cos(lat) times smaller — round texels became radial ellipses.
 *
 * ONE CELL PER PIXEL: the direction's cube-face cell is fetched from the cell
 * table (start, count) and its records looped (two RGBA32F texels each); the
 * bucketing inserted every star into every cell its reach disc touches, so
 * no neighbourhood walk is needed and nothing pops at a cell edge — as long
 * as the shader draws NOTHING past `reach`, which the window term enforces
 * (the no-popping law; the reach formula lives in star_catalog.ts).
 *
 * NO-CYCLE RULE: parameters in, node out; this file imports nothing from
 * SkyModule.
 */
import {
  Fn, float, int, vec3, ivec2, abs, floor, min, max, clamp, exp, length, fwidth,
  smoothstep, select, sin, time, textureLoad, Loop,
} from 'three/tsl';
import type { Texture } from 'three/webgpu';
import { STAR_SHRINK, STAR_HALO } from './star_catalog.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface StarFieldInput {
  /** The cell table: 6·G wide × G high RGBA32F, (entry start, count, 0, 0). */
  cells: Texture;
  /** The star records: `recordsWidth` wide RGBA32F, two texels per entry. */
  records: Texture;
  grid: number;
  recordsWidth: number;
  /** Loop cap per cell (the bucketing sorted each cell bright-first). */
  maxPerCell: number;
}

export interface StarFieldUniforms {
  /** Scintillation weight (airmass-scaled; 0 = steady). */
  airW: N;
  /** Master brightness gain (uniform). */
  gain: N;
}

/** Cube-face cell of a unit direction — the TSL mirror of star_catalog's `cubeCellOf`. */
export function cubeCellNode(d: N, G: number): { face: N; i: N; j: N } {
  const ax = abs(d.x), ay = abs(d.y), az = abs(d.z);
  const isX = ax.greaterThanEqual(ay).and(ax.greaterThanEqual(az));
  const isY = isX.not().and(ay.greaterThanEqual(az));
  const ma = select(isX, ax, select(isY, ay, az));
  const s = select(isX, d.y, d.x);
  const t = select(isX.or(isY), d.z, d.y);
  const face = select(isX, select(d.x.greaterThan(0), float(0), float(1)),
    select(isY, select(d.y.greaterThan(0), float(2), float(3)), select(d.z.greaterThan(0), float(4), float(5))));
  const i = clamp(floor(s.div(ma).add(1).mul(0.5 * G)), 0, G - 1);
  const j = clamp(floor(t.div(ma).add(1).mul(0.5 * G)), 0, G - 1);
  return { face, i, j };
}

/** Unpack star_catalog's `packColor` (r·65536 + g·256 + b, each ×100). */
const unpackColor = (p: N): N => {
  const r = floor(p.div(65536));
  const rest = p.sub(r.mul(65536));
  const g = floor(rest.div(256));
  const b = rest.sub(g.mul(256));
  return vec3(r, g, b).mul(0.01);
};

/**
 * The star field's linear radiance along `viewDir` (unit, world frame).
 * Returns rgb to composite BEFORE extinction (stars are above the atmosphere).
 */
export function buildStarField(viewDir: N, inp: StarFieldInput, u: StarFieldUniforms): N {
  const G = inp.grid, RW = inp.recordsWidth;
  return Fn(() => {
    const d = viewDir;
    const { face, i, j } = cubeCellNode(d, G);
    const cell = textureLoad(inp.cells, ivec2(face.mul(G).add(i), j));
    const start = cell.x, count = cell.y;
    const n = min(count, float(inp.maxPerCell)).toInt();
    // Angular pixel footprint (chord per pixel) — the ≥1-px core floor so a
    // faint star dims rather than shrinks out of existence (the anti-shimmer law).
    const dAA = clamp(length(fwidth(d)), 0.00035, 0.02);
    const pxFloor = dAA.mul(0.75);
    const acc = vec3(0).toVar();
    Loop({ start: int(0), end: n, type: 'int', condition: '<' }, ({ i: k }: { i: N }) => {
      const e = start.toInt().add(k).mul(2).toFloat(); // first texel of the entry
      const ty = floor(e.div(RW));
      const tx = e.sub(ty.mul(RW));
      const t0 = textureLoad(inp.records, ivec2(tx, ty));
      const t1 = textureLoad(inp.records, ivec2(tx.add(1), ty));
      const sdir = t0.xyz, sigma = t0.w;
      const packed = t1.x, flux = t1.y, reach = t1.z, phase = t1.w;
      const dist = length(d.sub(sdir)); // chord ≈ angle for these radii
      const sigE = max(sigma.mul(STAR_SHRINK), pxFloor);
      // Energy-conserving Gaussian: the flux is what the photo measured, the
      // tighter core makes the peak brighter (HDR — bloom picks it up).
      const peak = flux.div(sigE.mul(sigE).mul(2 * Math.PI));
      const core = exp(dist.mul(dist).div(sigE.mul(sigE).mul(-2)));
      const halo = exp(dist.div(sigma).negate()).mul(STAR_HALO);
      // Draw nothing past the reach the bucketing used (fade over the last 30 %).
      const win = float(1).sub(smoothstep(reach.mul(0.7), reach, dist));
      // Scintillation: two incommensurate sines, per-star phase, airmass-weighted.
      const tw = float(1).add(
        sin(time.mul(float(2.2).add(phase.mul(5.0))).add(phase.mul(40))).mul(0.22)
          .add(sin(time.mul(float(6.1).add(phase.mul(9.0))).add(phase.mul(17))).mul(0.12))
          .mul(u.airW),
      );
      acc.addAssign(unpackColor(packed).mul(peak.mul(core.add(halo)).mul(win).mul(tw)));
    });
    return acc.mul(u.gain);
  })();
}
